import { ButtonLink } from '../components/primitives';
import { getIdeDownloadUrlWin, getIdeGithubReleasesUrl } from '../lib/ideDownload';
import {
  AlertIcon,
  AppIconPlaceholder,
  ChipIcon,
  CubeIcon,
  DownloadArrowIcon,
  GitHubIcon,
  InfoIcon,
  LaptopIcon,
  ListIcon
} from './DownloadIcons';

/**
 * Windows download pane — card + alerts (left) and SmartScreen instructions (right).
 */
export function WinDownloadPane() {
  const downloadUrl = getIdeDownloadUrlWin();
  const githubReleasesUrl = getIdeGithubReleasesUrl('win');

  return (
    <div className="lp-download-layout">
      <div className="lp-download-main">
        <div className="lp-download-card lp-download-surface">
          <div className="lp-download-card-body">
            <div className="lp-download-card-head">
              <div className="lp-download-card-icon-wrap">
                <AppIconPlaceholder className="lp-download-card-icon" />
              </div>
              <div className="lp-download-card-intro">
                <div className="lp-download-card-title-row">
                  <h2 className="lp-download-card-title">Download for Windows</h2>
                  <span className="lp-download-badge">Unsigned build</span>
                </div>
                <p className="lp-download-card-desc">
                  Early tester build for Windows (64-bit). Requires Windows 10 or later.
                </p>
              </div>
            </div>

            {downloadUrl ? (
              <ButtonLink
                href={downloadUrl}
                variant="primary"
                size="lg"
                className="lp-download-primary-btn"
                data-testid="lp-download-win"
                rel="noopener noreferrer"
              >
                <DownloadArrowIcon className="lp-download-btn-icon" />
                Download .exe
              </ButtonLink>
            ) : (
              <p className="lp-download-unavailable" data-testid="lp-download-unavailable-win">
                Download is not configured yet. Ask your admin to set{' '}
                <code>VITE_IDE_DOWNLOAD_URL_WIN</code> on the site build.
              </p>
            )}

            <a
              className="lp-download-github-link"
              href={githubReleasesUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <GitHubIcon className="lp-download-link-icon" />
              View on GitHub Releases →
            </a>
          </div>

          <div className="lp-download-card-foot">
            <div className="lp-download-spec">
              <ChipIcon className="lp-download-spec-icon" />
              <div>
                <span className="lp-download-spec-value">Windows 10+</span>
                <span className="lp-download-spec-label">Minimum</span>
              </div>
            </div>
            <div className="lp-download-spec">
              <LaptopIcon className="lp-download-spec-icon" />
              <div>
                <span className="lp-download-spec-value">x64</span>
                <span className="lp-download-spec-label">64-bit</span>
              </div>
            </div>
            <div className="lp-download-spec">
              <CubeIcon className="lp-download-spec-icon" />
              <div>
                <span className="lp-download-spec-value">Unsigned build</span>
                <span className="lp-download-spec-label">For early testers</span>
              </div>
            </div>
          </div>
        </div>

        <div className="lp-download-alert lp-download-surface" data-testid="lp-download-callout-win">
          <AlertIcon className="lp-download-section-icon" />
          <div>
            <p className="lp-download-section-title">Unsigned build for early testers</p>
            <p className="lp-download-alert-body">
              SmartScreen will keep warning until the build is code-signed with an EV certificate
              (planned). This is a Windows-side trust prompt, not malware.
            </p>
          </div>
        </div>
      </div>

      <aside className="lp-download-sidebar">
        <div className="lp-download-sidebar-card lp-download-surface">
          <div className="lp-download-section-head">
            <ListIcon className="lp-download-section-icon" />
            <h3 className="lp-download-section-title">Instructions</h3>
          </div>
          <ol className="lp-download-sidebar-steps">
            <li>
              After download, double-click <strong>Penrose-IDE-&lt;version&gt;-win.exe</strong>.
            </li>
            <li>
              Windows SmartScreen will show <em>&ldquo;Windows protected your PC.&rdquo;</em>{' '}
              Click <strong>More info</strong>.
            </li>
            <li>
              Click <strong>Run anyway</strong> to launch the installer.
            </li>
            <li>
              Follow the installer to completion, then launch <strong>Penrose IDE</strong> from
              the Start menu.
            </li>
          </ol>
        </div>

        {!downloadUrl ? (
          <div className="lp-download-sidebar-card lp-download-surface">
            <div className="lp-download-section-head">
              <InfoIcon className="lp-download-section-icon" />
              <h3 className="lp-download-section-title">Note</h3>
            </div>
            <p className="lp-download-sidebar-note">
              Download is not configured yet. Ask your admin to set{' '}
              <code>VITE_IDE_DOWNLOAD_URL_WIN</code> on the site build.
            </p>
          </div>
        ) : null}
      </aside>
    </div>
  );
}
